import { useEffect, useState } from "react";
import { IMedia, LayoutCommonProps } from "../../../types";
import * as Styled from "./Layout7.styled";
import { StarFill } from "react-bootstrap-icons";
import { SidebarCard } from "../../media-card/small-card";
import { LayoutHeader } from "../LayoutHeader";
import LayoutModal from "../../media-card/FullScreenModal";
import { toTitleCase, truncateWords } from "../../../libs/utils";
import { StarRating } from "../../star-rating";
export const Layout7: React.FC<LayoutCommonProps> = ({
    mediaItems,
    mediaRating,
    onWatchClick,
    onRatingSuccess,
    isSignedIn,
    sessionToken,
    activePlans,
    hasFullAccess,
    freeTrialPlanIds,
    playlistTitle,
    enableCompetitionMode,
    enableRatingControls,
    refreshMediaRatings
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mainMedia, setMainMedia] = useState<IMedia | undefined>(undefined);

    const handleRatingSuccessLocal = () => {
        onRatingSuccess();
        refreshMediaRatings?.()
    };
    useEffect(() => {
        if (mediaItems.length && !mainMedia) {
            const firstMedia = mediaItems[0];
            setMainMedia(firstMedia);
        }
    }, [mediaItems, mainMedia]);

    const handleSeeAllClick = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const onCardClick = (item: IMedia) => {
        setMainMedia(item);
    };

    // Add this function here
    const getLabel = (item: IMedia) => {
        if (!item?.planDetails?.length) return 'Watch Now';
        if (!isSignedIn) return 'Get Access';

        const hasEligiblePlan =
            item.planDetails?.some((pd) =>
                activePlans.some((plan) => plan.id === pd.id)
            ) || freeTrialPlanIds.includes(item.planDetails?.[0]?.id) || hasFullAccess;

        return hasEligiblePlan ? 'Watch Now' : 'Purchase Plan';
    };

    if (!mediaItems.length || !mainMedia) return null;

    const remainingMedia = mediaItems;
    const chunkArray = (arr: IMedia[], size: number) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
            chunks.push(arr.slice(i, i + size));
        }
        return chunks;
    };

    const groupedRows = chunkArray(remainingMedia.slice(0, 8), 2);

    return (
        <>
            <LayoutHeader
                title={playlistTitle}
                itemCount={mediaItems?.length}
                onSeeAll={handleSeeAllClick}
                padding="50px 24px 20px 0px"
                marginTop="0px"
                marginBottom="10px"
            />
            {groupedRows.map((row, rowIndex) => (
                <div key={rowIndex}>
                    <Styled.Grid>
                        {row.map((item) => {
                            const hasEligiblePlan =
                                isSignedIn &&
                                (!!item.planDetails?.length
                                    ? !!activePlans.filter((plan) =>
                                        item.planDetails?.some((pd) => pd?.id === plan.id)
                                    ).length ||
                                    freeTrialPlanIds.includes(item.planDetails[0]?.id) ||
                                    hasFullAccess
                                    : true);

                            return (
                                <Styled.CardWrapper key={item.id}>
                                    <SidebarCard
                                        mediaItem={item}
                                        cardClick={() => onCardClick(item)}
                                        isSelected={false}
                                        mediaRating={mediaRating.find((m) => m.mediaId === item.mediaId) || 0}
                                        enableRatingControls
                                        isSignedIn={isSignedIn}
                                        hasEligiblePlan={hasEligiblePlan}
                                        onWatchClick={onWatchClick}
                                        onRatingSuccess={onRatingSuccess}
                                        sessionToken={sessionToken}
                                        applyResponsive={true}
                                        enableCompetitionMode={enableCompetitionMode}
                                    />
                                    <Styled.ContentWrapper>
                                        <Styled.Title title={item.title}>
                                            {typeof item.title === "string" ? truncateWords(item.title, 50) : ""}
                                        </Styled.Title>
                                        <Styled.RatingAndDirectorBlock>
                                            {(() => {
                                                const matchedRating = mediaRating.find((m) => m.mediaId === item.mediaId)?.rating;
                                                const rating = matchedRating ?? 0;
                                                const directors = toTitleCase(item?.directors || "");

                                                const shouldShowRating = !enableCompetitionMode && (rating || rating === 0);
                                                const shouldShowDirector = !!directors;

                                                if (!shouldShowRating && !shouldShowDirector) return null;

                                                return (
                                                    <Styled.DirectorRow>
                                                        {/* Show rating only when competition mode is OFF */}
                                                        {shouldShowRating && (
                                                            <>
                                                                <StarFill color="#000000" />
                                                                {`${rating}/5`}
                                                            </>
                                                        )}

                                                        {/* Always show director name if present */}
                                                        {shouldShowDirector && <span>{directors}</span>}
                                                    </Styled.DirectorRow>
                                                );
                                            })()}

                                            {/* Show rating input regardless of competition mode, if allowed */}
                                            {isSignedIn && enableRatingControls && (enableCompetitionMode || hasEligiblePlan) && (
                                                <StarRating
                                                    mediaItem={item}
                                                    mediaRating={mediaRating.find((m) => m.mediaId === item.mediaId)?.rating ?? 0}
                                                    sessionToken={sessionToken}
                                                    onRatingSuccess={handleRatingSuccessLocal}
                                                />
                                            )}
                                        </Styled.RatingAndDirectorBlock>


                                        <Styled.TrailerLink onClick={() => onWatchClick(item)}>
                                            {getLabel(item)} →
                                        </Styled.TrailerLink>
                                    </Styled.ContentWrapper>
                                </Styled.CardWrapper>
                            );
                        })}
                    </Styled.Grid>
                    {rowIndex !== groupedRows.length - 1 && <Styled.RowDivider />}
                </div>
            ))}



            {/* Modal with Lazy Loaded Cards */}
            <LayoutModal
                title={playlistTitle}
                show={isModalOpen}
                onHide={handleCloseModal}
                remainingMedia={remainingMedia}
                onCardClick={onCardClick}
                isSignedIn={isSignedIn}
                activePlans={activePlans}
                hasFullAccess={hasFullAccess}
                freeTrialPlanIds={freeTrialPlanIds}
                mediaRating={mediaRating}
                onWatchClick={onWatchClick}
                onRatingSuccess={onRatingSuccess}
                sessionToken={sessionToken}
            />
        </>
    );
};
